<script setup lang="ts">
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faArrowRightLong } from '@fortawesome/duotone-regular-svg-icons'
import { gradeTypeNames } from '~/utils/gradeSorter'
import useAuthStore from '~/stores/auth'
import { PendoTrackName } from '~/models/Pendo'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo'
import { useContentHelper } from '~/composables/useContentHelper'
import { useFavoritesApi } from '~/api/favoritesApi'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'

const { section } = defineProps<{ section: ContentSectionStandalone }>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { buildResourceUrl } = useUrlBuilder()
const { pendoTrack, pendoFlushNow } = usePendo()
const { selectedGradeType } = storeToRefs(useAuthStore())
const { isAuthenticated } = storeToRefs(useAuthStore())
const { isFile, isPdf } = useContentHelper()
const { getFavoriteList } = useFavoritesApi()
const router = useRouter()

const { data: content } = useQuery({
  enabled: computed(() => Number(section.resource?.destinationContentId) > 0),
  queryKey: computed(() => ['content-id', 'dont-invalidate-on-grade-change', section.contentId]),
  queryFn: async () => (await findContents({
    contentIdCriterion: [Number(section.resource!.destinationContentId)],
    mainLocationCriterion: true,
  }, 1))[0],
  staleTime: Infinity,
  retry: 1,
})

const { data: list } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!section.listId),
  queryKey: computed(() => ['favorite-list', section.listId]),
  queryFn: () => getFavoriteList(section.listId!),
  staleTime: Infinity,
  retry: 1,
})

const listRoute = computed(() => list?.value ? router.resolve({ name: 'favorite_list', params: { listId: list.value.id } }).href : '')
const link = computed(() => content?.value ? buildResourceUrl(content.value) : listRoute.value)

const isAnchor = computed(() => !link.value.startsWith('/'))
const transitionEffect = computed(() => !content.value || (!isFile(content.value) && !isPdf(content.value)))

const track = () => {
  if ((!content?.value && !list?.value) || !selectedGradeType.value)
    return
  pendoTrack(PendoTrackName.BannerArticle, {
    title: content.value?.title ?? list.value?.name ?? '',
    gradeType: gradeTypeNames[selectedGradeType.value],
    url: link.value,
  })
  pendoFlushNow()
}

const subtitle = computed(() => {
  const subject = section.subjects[0] ? t(`metadata.subjects.${section.subjects[0]}`) : null
  const label = list.value ? t('favorite.list') : null
  return [subject, label].filter(Boolean).join(' | ')
})

const color = computed(() => section.colorPair.text)
const backgroundColor = computed(() => section.colorPair.background)
</script>

<template>
  <LinkOrAnchor
    v-if="link"
    :link
    :is-anchor
    :transition-effect
    class="group ring-offset-seagreen-50 hover:ring-seagreen-10 grid h-full grid-cols-3 items-center overflow-hidden rounded-lg text-left ring-offset-2 transition hover:ring focus-visible:ring md:min-h-64"
    :style="{ backgroundColor }"
    @click="track"
  >
    <div class="col-span-full aspect-video size-full sm:col-span-1">
      <img
        class="size-full object-cover"
        :src="section.thumbnail"
        :alt="section.thumbnailAlt"
      >
    </div>
    <div class="col-span-full px-6 py-8 sm:col-span-2">
      <p
        v-if="subtitle"
        class="mb-4 max-w-prose text-xs font-medium tracking-wider uppercase"
        :style="{ color }"
        v-text="subtitle"
      />
      <span
        class="mr-2 max-w-prose text-xl font-bold"
        :style="{ color }"
        v-text="section.title"
      />
      <RichTextRenderer
        v-if="section.description"
        class="mt-2 max-w-prose text-base"
        :style="{ color }"
        :text="section.description"
      />
      <font-awesome-icon
        :icon="faArrowRightLong"
        size="xl"
        class="mt-4 transition-all group-hover:ml-2"
        :style="{ color }"
      />
    </div>
  </LinkOrAnchor>
</template>

<style scoped>
@reference "../../../styles/tailwind.css"
:deep(a) {
  color: v-bind(color);
  font-weight: 600;
  border: 1px solid black;
}
:deep(a):hover {
  background: v-bind(color);
  color: v-bind(backgroundColor);
  transition: 0.1s;
  border-radius: 1px;
}
:deep(a):focus-visible {
  @apply ring;
}
</style>
